import React, { useState, useContext, useEffect, useCallback, useRef, useMemo } from 'react';
import log from 'app/home/general-components/Logging'
import { v4 as uuidv4 } from 'uuid';

export const HistoryStackContext = React.createContext(undefined);
export const CurrentPageContext = React.createContext(undefined);

export function HistoryStackProvider(props) {
    const pageStackRef = useRef([]);
    const [pageStack, setPageStack] = useState([]);

    // isOverlayPage is false for pages that are just a state and we want to support back for them
    const push = useCallback((path, state = undefined, isOverlayPage = true) => { // window.history.pushState()
        let innerState = {};
        if (state !== undefined)
            innerState = state;

        let pageUUID = uuidv4();

        let pageToAdd = { path, innerState, pageUUID, isOverlayPage, isBeingRemoved: false };
        let historyPageState = { pageUUID };

        const newPageStack = [...pageStackRef.current];
        newPageStack.push(pageToAdd);
        pageStackRef.current = newPageStack;
        setPageStack(pageStackRef.current);

        window.history.pushState(historyPageState, "", "/#" + String(pageUUID));
    }, []);

    const findPageInStack = useCallback(({ path }) => {
        for (let i = 0; i < pageStack.length; i++) {
            const page = pageStack[i];
            if (page.path === path) {
                return page;
            }
        }
        return undefined;
    }, [pageStack]);

    const findAllPagesInStack = useCallback(({ pageUUID, path, pathPrefix }) => {
        let pages = [];
        for (let i = 0; i < pageStack.length; i++) {
            const page = pageStack[i];
            if (
                ((pageUUID !== undefined) && (page.pageUUID === pageUUID)) ||
                ((path !== undefined) && (page.path === path)) ||
                ((pathPrefix !== undefined) && (page.path.startsWith(pathPrefix)))
            ) {
                pages.push(page);
            }
        }
        return pages;
    }, [pageStack]);

    const checkAndBack = useCallback(({ pageUUID, path, pathPrefix }, multiplePages = false) => { // window.history.back()
        if (multiplePages !== false && multiplePages !== true && multiplePages !== undefined) {
            console.log("unsupported argument for checkAndBack", multiplePages);
            return;
        }
        let numberOfBacks = 0;
        if (pageStackRef.current.length > 0) {
            for (let i = 1; i <= pageStackRef.current.length; i++) {
                const page = pageStackRef.current[pageStackRef.current.length - i];
                if (!page.isBeingRemoved) { // we can skip pages with isBeingRemoved and continue
                    if (
                        ((pageUUID !== undefined) && (page.pageUUID === pageUUID)) ||
                        ((path !== undefined) && (page.path === path)) ||
                        ((pathPrefix !== undefined) && (page.path.startsWith(pathPrefix)))
                    ) {
                        page.isBeingRemoved = true;
                        // window.history.back();
                        numberOfBacks += 1;
                    } else {
                        break; // should not issue back for this page, and all pages below it.
                    }
                    if (!multiplePages) {
                        break;
                    }
                }
            }
        }
        if (numberOfBacks > 0) {
            // console.log("window.history.go", -numberOfBacks);
            window.history.go(-numberOfBacks);
        }
    }, []);

    const back = useCallback((count = 1) => { // window.history.back()
        window.history.go(-count);
    }, []);

    const bottomPagePath = pageStack.length === 0 ? undefined : pageStack[0].path;
    const topPage = pageStack.length === 0 ? undefined : pageStack[pageStack.length - 1];
    const topPageSafe = topPage ? topPage : {};

    useEffect(() => {
        const onPop = (e) => {
            const newPageStack = [...pageStackRef.current];

            let numberOfPops = 1;
            if (e) {
                if (e.state) {
                    const finalPageUUID = e.state.pageUUID;
                    const indexOfFinalPage = newPageStack.findIndex((p) => p.pageUUID === finalPageUUID);
                    numberOfPops = (newPageStack.length - 1) - indexOfFinalPage;
                }
            }

            for (let i = 0; i < numberOfPops; i++) {
                newPageStack.pop();
            }
            pageStackRef.current = newPageStack;
            setPageStack(pageStackRef.current);
        };
        window.addEventListener('popstate', onPop);
        return () => {
            window.removeEventListener('popstate', onPop);
        }
    }, []);

    return (
        <HistoryStackContext.Provider value={
            {
                pageStack, bottomPagePath, findPageInStack, findAllPagesInStack,
                push, back, checkAndBack,
            }}>
            <CurrentPageContext.Provider
                // used as a fallback if CurrentPageContext is not exsit in a path 
                // for popups for example
                key={"base_current_page_for_fallback"}
                value={{
                    path: topPageSafe.path,
                    state: topPageSafe.innerState,
                    id: topPageSafe.pageUUID,
                    dismiss: () => { checkAndBack({ pageUUID: topPageSafe.pageUUID }) },
                    dontUseBackToDissmiss: false,
                    inOverlayPage: false,
                }}>
                <HistoryStackPrinter />
                {props.children}
            </CurrentPageContext.Provider>
        </HistoryStackContext.Provider>
    )
}

function HistoryStackPrinter() {
    const o = useContext(HistoryStackContext);
    useEffect(() => {
        log("history stack:", o.pageStack);
    }, [o.pageStack])

    return null;
}

export function useHistoryState(parentPageId, _initialState) {
    const hs = useContext(HistoryStackContext);
    const initialState = useState(_initialState)[0];

    const pathPrefix = "/history-state/" + String(parentPageId) + "/";

    const _allStates = useMemo(() => {
        return hs.findAllPagesInStack({ pathPrefix }).map((p) => p.innerState);
    }, [hs, pathPrefix]);
    const [allStates, setAllStates] = useState(_allStates);
    const [lockState, setLockState] = useState(false);
    useEffect(() => {
        if (!lockState) {
            setAllStates(_allStates);
        }
    }, [lockState, _allStates]);

    const [numberOfStates, currentState] = useMemo(() => {
        if (allStates.length > 0) {
            return [allStates.length, allStates[allStates.length - 1]]
        } else {
            return [0, initialState];
        }
    }, [allStates, initialState]);

    const [delayedCurrentState, setDelayedCurrentState] = useState(currentState);
    useEffect(() => {
        setTimeout(() => {
            setDelayedCurrentState(currentState);
        });
    }, [currentState]);

    const pushState = useCallback((state) => {
        let id = uuidv4();
        hs.push(pathPrefix + id, state, false);
    }, [hs, pathPrefix]);

    const back = useCallback((allPages) => {
        hs.checkAndBack({ pathPrefix }, allPages);
    }, [hs, pathPrefix]);

    return {
        currentState, delayedCurrentState, numberOfStates,
        pushState, back, setLockState
    };
}